import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  SerializedError,
} from "@reduxjs/toolkit";
import httpRequest from "../helpers/httpRequest";
import { IRedDots } from "src/types/GlobalInterfaces";

export const getIndicators = createAsyncThunk(
  "INDICATORS_GET",
  async (endPoints: string, { rejectWithValue }) => {
    try {
      const { res, err } = await httpRequest({ path: endPoints });
      if (err) {
        return rejectWithValue(err);
      }
      return res;
    } catch (error) {
      // Safely handle the error object
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

// Initial state of the inidcators slice
const initialState: {
  allIndicators: any | null;
  indicatorLoader: boolean;
  indicatorError: string | SerializedError | null;
  redDots: IRedDots;
} = {
  allIndicators: null,
  indicatorLoader: false,
  indicatorError: null,
  redDots: {
    reportNotified: 0,
    resultNotified: 0,
  }
};

// Create the inidcators slice
export const redDots = createSlice({
  name: "INDICATORS_SLICE",
  initialState,
  reducers: {
    removeAllIndicators: (state) => {
      state.allIndicators = null;
    },
    updateRedDots: (state, action: PayloadAction<Partial<IRedDots>>) => {
      state.redDots = { ...state.redDots, ...action.payload };
    },
    updateIndicators: (state, action: PayloadAction<Partial<any>>) => {
      if (state.allIndicators) {
        state.allIndicators = {
          ...state.allIndicators,
          ...Object.entries(action.payload).reduce((acc, [key, value]) => {
            if (
              typeof value === "number" &&
              typeof state.allIndicators[key] === "number"
            ) {
              (acc as any)[key] = state.allIndicators[key] + value;
            } else {
              (acc as any)[key] = value;
            }
            return acc;
          }, {}),
        };
      } else {
        // If allIndicators is null, initialize it with the payload
        state.allIndicators = { ...action.payload };
      }
    },
    updateIndicatorsCount: (state, action: PayloadAction<Partial<any>>) => {
      if (state.allIndicators) {
        state.allIndicators = {
          ...state.allIndicators,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getIndicators.pending, (state) => {
        state.indicatorLoader = true;
        state.indicatorError = null; // Reset error on new request
      })
      .addCase(getIndicators.fulfilled, (state, action: PayloadAction<any>) => {
        state.indicatorLoader = false;
        state.allIndicators = action.payload?.data || null; // Store the fetched inidcators data
      })
      .addCase(getIndicators.rejected, (state, action) => {
        state.indicatorLoader = false;
        state.indicatorError = action.payload || action.error; // Store the error message or SerializedError
      });
  },
});

// Export action creators
export const { removeAllIndicators, updateIndicators, updateRedDots, updateIndicatorsCount } = redDots.actions;

// Export the reducer to be used in the store
export default redDots.reducer;
